import type { LessonProgress, Material } from '@facecontrol/shared';
import { useRef, useState } from 'react';
import { imageSrc, plural } from '../lib/format';
import { IconArrow, IconCheck } from './icons';

const NEW_FOR_MS = 72 * 3_600_000;

export function lessonState(material: Material, progress?: LessonProgress) {
  const done = !!progress?.completedAt;
  const started = !done && (progress?.position ?? 0) > 0;
  const isNew = !done && !started && Date.now() - new Date(material.createdAt).getTime() < NEW_FOR_MS;
  return { done, started, isNew, pct: Math.round((progress?.position ?? 0) * 100) };
}

export const kindLabel = (m: Material) =>
  m.contentType === 'video' ? (m.video.length > 1 ? `Видео-урок · ${plural(m.video.length, 'часть', 'части', 'частей')}` : 'Видео-урок') : 'Фотогайд';

/** A photograph that fades in when it arrives and falls back to the lesson's initial on ivory. */
export function Photo({ src, name, eager = false, className = 'photo' }: { src: string | null; name: string; eager?: boolean; className?: string }) {
  const [state, setState] = useState<'loading' | 'ok' | 'broken'>(src ? 'loading' : 'broken');
  // the address can arrive after mount (public programme loads late): start over when it changes
  const [seen, setSeen] = useState(src);
  if (seen !== src) {
    setSeen(src);
    setState(src ? 'loading' : 'broken');
  }
  return (
    <div className={`${className}${state === 'broken' ? ' is-empty' : ''}`}>
      {src && state !== 'broken' && (
        <img src={src} alt="" className={state === 'ok' ? 'is-loaded' : ''} loading={eager ? 'eager' : 'lazy'} decoding="async" fetchPriority={eager ? 'high' : 'auto'} onLoad={() => setState('ok')} onError={() => setState('broken')} />
      )}
      {state === 'broken' && <span className="photo__mono">{name.slice(0, 1)}</span>}
    </div>
  );
}

interface Props {
  material: Material;
  /** 1-based position in its month; 0 hides «Урок N» */
  number: number;
  progress?: LessonProgress;
  eager?: boolean;
  onOpen: (m: Material, el: HTMLElement) => void;
}

/** Editorial lesson tile: photo, «Урок N», the title in the serif, one line, the status, a fine rule under it. */
export function MaterialCard({ material, number, progress, eager = false, onOpen }: Props) {
  const ref = useRef<HTMLButtonElement>(null);
  const { done, started, isNew, pct } = lessonState(material, progress);
  const state = done ? ', пройден' : started ? `, просмотрено ${pct}%` : isNew ? ', новый урок' : '';
  return (
    <button ref={ref} type="button" className="tile" onClick={() => ref.current && onOpen(material, ref.current)} aria-label={`Открыть: ${material.name}${state}`}>
      <Photo src={imageSrc(material.preview)} name={material.name} eager={eager} className="tile__photo photo" />
      <span className="tile__num">{number > 0 ? `Урок ${number}` : kindLabel(material)}</span>
      <span className="tile__title">{material.name}</span>
      {material.subtitle && <span className="tile__sub">{material.subtitle}</span>}
      <span className="tile__state">
        {done ? (
          <span className="st st--done">
            <i aria-hidden="true">
              <IconCheck />
            </i>
            Пройден
          </span>
        ) : started ? (
          <span className="st st--progress">
            <span className="bar" aria-hidden="true">
              <i style={{ width: `${pct}%` }} />
            </span>
            Просмотрено {pct}%
          </span>
        ) : isNew ? (
          <span className="st st--new">Новый урок</span>
        ) : (
          <span className="st st--kind">
            {number > 0 ? kindLabel(material) : 'Открыть'} <IconArrow />
          </span>
        )}
      </span>
    </button>
  );
}

export function CardSkeleton() {
  return (
    <div className="tile tile--sk" aria-hidden="true">
      <div className="tile__photo sk" />
      <div className="sk sk__line" />
      <div className="sk sk__line sk__line--short" />
    </div>
  );
}
